import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createBlock as _createBlock, withCtx as _withCtx, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "d-flex flex-column" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("span", { class: "color-red" }, "*", -1)
const _hoisted_3 = {
  key: 1,
  class: "d-flex flex-column align-items-center justify-content-center"
}
const _hoisted_4 = {
  key: 2,
  class: "d-flex flex-column margin-top-10"
}
const _hoisted_5 = /*#__PURE__*/_createElementVNode("strong", null, [
  /*#__PURE__*/_createTextVNode(/*#__PURE__*/_toDisplayString("Flows")),
  /*#__PURE__*/_createElementVNode("span", { class: "color-red" }, "*")
], -1)
const _hoisted_6 = { class: "d-flex flex-column margin-top-10" }

import { ElSelect, ElOption } from "element-plus";
import { JsonForms } from "@jsonforms/vue";
import { createAjv } from "@jsonforms/core";
import { onBeforeMount, provide, ref, watch } from "vue";
import { cloneDeep } from "lodash-es";
import {
  defaultRenderers as renderers,
  styles,
} from "@/core/json-forms/json-forms";
import i18n from "@/core/i18n/i18n";
import { secretFieldNames } from "@/connection/utils/authValidation";


export default /*#__PURE__*/_defineComponent({
  __name: 'AuthenticationForm',
  props: {
    authList: {},
    data: {},
    multiple: { type: Boolean },
    authResult: {},
    connectionId: {},
    verified: { type: Boolean },
    clicked: { type: Boolean }
  },
  emits: [
  "update",
  "errors",
  "open-url",
  "disconnect-from-oauth",
  "verify-login",
],
  setup(__props: any, { emit: __emit }) {

provide("styles", styles);
const ajv = createAjv({ useDefaults: true });
const { t } = i18n.global;

const props = __props;

const emit = __emit;

const selectedAuthList = ref([]);

const selectedAuthMap = ref(new Map());

// AJV errors from the rendered forms, kept per scheme so a scheme the user switches away
// from stops contributing, and reported upwards for the save-time check. JSON Forms emits
// an initial change event on mount, so an untouched form is covered too.
const errorMap = ref(new Map());

// Errors reported by the nested form of a scheme that has OAuth flows, keyed by that scheme.
const flowErrorMap = ref(new Map());

// Values entered for schemes the user has switched away from, keyed by scheme name,
// so re-selecting one restores its fields rather than clearing them.
const retainedAuthData = ref(new Map());

const selectedAuthItem = ref("");

function convertToNormalText(text) {
  if (text) {
    const result = text
      .toLowerCase()
      .replace(/^_*(.)|_+(.)/g, (s, c, d) =>
        c ? c.toUpperCase() : ` ${d.toUpperCase()}`
      );

    return result.charAt(0).toUpperCase() + result.slice(1);
  }
  return "";
}

// Drop entries for schemes that are no longer selected, then report what is left. Their
// forms are unmounted and emit nothing, so stale errors would otherwise block a save.
function emitErrors() {
  [errorMap.value, flowErrorMap.value].forEach((map) => {
    [...map.keys()].forEach((key) => {
      if (!selectedAuthMap.value.has(key)) {
        map.delete(key);
      }
    });
  });
  emit("errors", [
    ...[...errorMap.value.values()].flat(),
    ...[...flowErrorMap.value.values()].flat(),
  ]);
}

function onFlowErrors(errors, key) {
  flowErrorMap.value.set(key, errors ?? []);
  emitErrors();
}

function onDataChange(event, key) {
  const entry = selectedAuthMap.value.get(key);
  if (!entry) {
    return;
  }
  errorMap.value.set(key, event.errors ?? []);
  emitErrors();
  // The backend strips encrypted values on read (they arrive as null); showing "*****"
  // makes an untouched field round-trip as null so the server keeps the stored value.
  const secretFields = secretFieldNames(entry.schema);
  Object.entries(event.data).forEach(([k, v]: any[]) => {
    if (secretFields.includes(k) && v === null) {
      event.data[k] = "*****";
    }
  });
  entry.data = event.data;
  emit("update", selectedAuthMap.value);
}

function updateData(data, key) {
  selectedAuthMap.value.get(key).flowData = cloneDeep(data);
  emit("update", selectedAuthMap.value);
}
function updateFlowData(flowData) {
  flowData.forEach((key) => {
    if (
      key.data &&
      (key.data.clientId === null ||
        key.data.clientSecret === null ||
        key.data.password === null)
    ) {
      key.data.clientId = "*****";
      key.data.clientSecret = "*****";
      key.data.password = "*****";
    }
  });
  return flowData;
}
function selectedAuthItemChange() {
  // Keep what was already entered for the scheme being switched away from, so
  // going (for example) basic -> oauth -> basic restores those values instead of
  // presenting empty fields. Only the selected scheme is submitted, so retaining
  // the others costs nothing.
  selectedAuthMap.value.forEach((value, key) => {
    retainedAuthData.value.set(key, {
      data: value.data,
      flowData: value.flowData,
    });
  });
  selectedAuthMap.value.clear();
  if (selectedAuthItem.value) {
    props.authList.forEach((item) => {
      if (item.name === selectedAuthItem.value) {
        const retained = retainedAuthData.value.get(item.name);
        selectedAuthMap.value.set(item.name, {
          name: item.name,
          schema: item.uiSchema,
          // Supplied by the backend so callers need no scheme-to-type mapping.
          authenticationType: item.authenticationType,
          requiresBrowserAuthentication: item.requiresBrowserAuthentication
            ? item.requiresBrowserAuthentication
            : false,
          flows: item.flows,
          data: retained ? retained.data : {},
          flowData: retained ? retained.flowData : new Map(),
        });
      }
    });
  }
  emit("update", selectedAuthMap.value);
  emitErrors();
}

function openUrl() {
  emit("open-url");
}

function disconnectFromOAuth() {
  emit("disconnect-from-oauth");
}

function verifyLogin() {
  emit("verify-login");
}

function removeAuthElement(oldValue, newValue) {
  if (newValue.length < oldValue.length) {
    const dif = oldValue.filter((data) => !newValue.includes(data));
    dif.forEach((key) => {
      selectedAuthMap.value.delete(key);
    });
  }
}

watch(
  () => selectedAuthList.value,
  (newValue, oldValue) => {
    if (selectedAuthList.value.length !== 0) {
      props.authList.forEach((item) => {
        if (
          selectedAuthList.value.findIndex((idx) => item.name === idx) > -1 &&
          !selectedAuthMap.value.has(item.name)
        ) {
          selectedAuthMap.value.set(item.name, {
            name: item.name,
            schema: item.uiSchema,
            authenticationType: item.authenticationType,
            requiresBrowserAuthentication: item.requiresBrowserAuthentication
              ? item.requiresBrowserAuthentication
              : false,
            flows: item.flows,
            data: {},
            flowData: new Map(),
          });
        }
      });
    }
    removeAuthElement(oldValue, newValue);
    emit("update", selectedAuthMap.value);
    emitErrors();
  }
);

onBeforeMount(() => {
  if (props.data.size !== 0) {
    if (props.multiple) {
      props.data.forEach((_value, key) => {
        selectedAuthList.value.push(key);
      });
      selectedAuthMap.value = cloneDeep(props.data);
    } else {
      props.data.forEach((_value, key) => {
        selectedAuthItem.value = key;
      });
      selectedAuthMap.value = cloneDeep(props.data);
    }
  } else if (!window.location.href.includes("create")) {
    props.authList.forEach((key) => {
      selectedAuthItem.value = key.name;
      selectedAuthItemChange();
    });
  }
});

return (_ctx: any,_cache: any) => {
  const _component_AuthenticationForm = _resolveComponent("AuthenticationForm", true)!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      (_ctx.multiple)
        ? (_openBlock(), _createBlock(_unref(ElSelect), {
            key: 0,
            "test-id": "authentication-list",
            id: "authentication-list",
            modelValue: selectedAuthList.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedAuthList).value = $event)),
            multiple: "",
            "collapse-tags": "",
            size: "small"
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.authList, (authItem) => {
                return (_openBlock(), _createBlock(_unref(ElOption), {
                  value: authItem.name,
                  key: authItem.name,
                  label: convertToNormalText(authItem.name)
                }, null, 8, ["value", "label"]))
              }), 128))
            ]),
            _: 1
          }, 8, ["modelValue"]))
        : (_openBlock(), _createBlock(_unref(ElSelect), {
            key: 1,
            modelValue: selectedAuthItem.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedAuthItem).value = $event)),
            onChange: selectedAuthItemChange,
            "collapse-tags": "",
            size: "small"
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.authList, (authItem) => {
                return (_openBlock(), _createBlock(_unref(ElOption), {
                  value: authItem.name,
                  key: authItem.name,
                  label: convertToNormalText(authItem.name)
                }, null, 8, ["value", "label"]))
              }), 128))
            ]),
            _: 1
          }, 8, ["modelValue"]))
    ]),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedAuthMap.value, ([key, value]) => {
      return (_openBlock(), _createElementBlock("div", {
        class: "d-flex flex-column margin-top-10",
        key: value.name
      }, [
        _createElementVNode("strong", null, [
          _createTextVNode(_toDisplayString(convertToNormalText(key)), 1),
          _hoisted_2
        ]),
        (value.schema)
          ? (_openBlock(), _createBlock(_unref(JsonForms), {
              data: value.data,
              key: value.name,
              schema: value.schema,
              ajv: _unref(ajv),
              renderers: _unref(renderers),
              onChange: ($event: any) => (onDataChange($event, key))
            }, null, 8, ["data", "schema", "ajv", "renderers", "onChange"]))
          : _createCommentVNode("", true),
        (value.requiresBrowserAuthentication)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              (_ctx.authResult.state === 'CONNECTED')
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    class: "google-button",
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (disconnectFromOAuth()))
                  }, _toDisplayString(_unref(t)("Common.SignOut")), 1))
                : _createCommentVNode("", true),
              (_ctx.clicked && _ctx.connectionId)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 1,
                    class: "google-button margin-top-10",
                    onClick: _cache[3] || (_cache[3] = ($event: any) => (verifyLogin()))
                  }, _toDisplayString(!_ctx.verified ? _unref(t)("Common.Verify") : _unref(t)("Common.Verified")), 1))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true),
        (value.flows)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _hoisted_5,
              _createElementVNode("div", _hoisted_6, [
                _createVNode(_component_AuthenticationForm, {
                  data: updateFlowData(value.flowData),
                  "auth-list": value.flows,
                  multiple: false,
                  "auth-result": _ctx.authResult,
                  verified: _ctx.verified,
                  clicked: _ctx.clicked,
                  "connection-id": _ctx.connectionId,
                  onUpdate: ($event: any) => (updateData($event, key)),
                  onErrors: ($event: any) => (onFlowErrors($event, key)),
                  onVerifyLogin: verifyLogin,
                  onDisconnectFromOauth: disconnectFromOAuth,
                  onOpenUrl: openUrl
                }, null, 8, ["data", "auth-list", "auth-result", "verified", "clicked", "connection-id", "onUpdate", "onErrors"])
              ])
            ]))
          : _createCommentVNode("", true)
      ]))
    }), 128))
  ]))
}
}

})